import { createFileRoute } from "@tanstack/react-router";
import { useQuery, type QueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { SegmentedControl, type SegmentedOption } from "@nexus/ui";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { usePageState } from "@/hooks/usePageState";
import { assertPagePermission } from "@/features/auth/lib/assert-page-permission";
import { hasActionPermission } from "@/features/auth/lib/has-action-permission";
import { EventsPage, EVENTS_PAGE_SIZE } from "@/features/events/components/EventsPage";
import {
  eventTimeRangeFromHours,
  eventTimeRangeHours,
  EVENT_TIME_RANGE_DEFAULT,
  EVENT_TIME_RANGE_VALUES,
} from "@/features/events/constants/event-time-ranges";
import { ChangelogDeploysPage } from "@/features/changelog/components/ChangelogDeploysPage";
import { ChangelogRoundupsPage } from "@/features/changelog/components/ChangelogRoundupsPage";
import { ChangelogSettingsPage } from "@/features/changelog/components/ChangelogSettingsPage";
import {
  CHANGELOG_SETTINGS_GROUPS,
  DEFAULT_CHANGELOG_SETTINGS_GROUP,
} from "@/features/changelog/lib/changelog-settings-groups";
import { AdminOpsSettingsPage } from "@/features/admin/components/AdminOpsSettingsPage";
import {
  DEFAULT_OPS_SETTINGS_GROUP,
  OPS_SETTINGS_GROUPS,
} from "@/features/admin/lib/ops-settings-groups";
import { ScheduledJobsPage } from "@/features/scheduled-jobs/components/ScheduledJobsPage";
import type { ScheduledJobsViewFilters } from "@/features/scheduled-jobs/lib/scheduled-job-filters";
import { LlmUsagePage } from "@/features/llm-usage/components/LlmUsagePage";
import { SCHEDULED_JOBS_TIME_RANGE_DEFAULT } from "@/features/scheduled-jobs/constants/scheduled-job-time-ranges";
import { ADMIN_OPS_FALLBACK } from "@/features/admin/lib/admin-ops-settings";
import {
  BigQueryUsagePage,
  type BigQueryUsageViewFilters,
} from "@/features/bigquery-usage/components/BigQueryUsagePage";
import {
  adminOpsSettingsQueryOptions,
  bigQueryUsageStatsQueryOptions,
  bigQueryUsageTimeseriesQueryOptions,
  BIGQUERY_USAGE_EXECUTION_SORT_COLUMNS,
  BIGQUERY_USAGE_TENANT_KINDS,
  type BigQueryUsageFilters,
  changelogDigestsQueryOptions,
  changelogQueryOptions,
  changelogSettingsQueryOptions,
  eventsInfiniteQueryOptions,
  eventsKeys,
  eventActionsInfiniteQueryOptions,
  eventsStatsQueryOptions,
  eventsTimeseriesQueryOptions,
  eventsUsersQueryOptions,
  llmUsageStatsQueryOptions,
  llmUsageTimeseriesQueryOptions,
  pageStateQueryOptions,
  PageAccessError,
  scheduledJobsQueryOptions,
  type EventJobSortBy,
  type EventJobStatus,
} from "@/lib/queries";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { useIsSuperTenant, usePermissions } from "@/features/auth/hooks/usePermissions";
import { PageError } from "@/features/auth/components/PageError";
import {
  AdminTabStrip,
  adminTabId,
  adminTabPanelId,
  type AdminTab,
} from "@/features/admin/components/AdminTabStrip";
import { AdminTabSkeleton } from "@/features/admin/components/AdminTabSkeleton";

/** The two halves of the settings tab. Deploys & roundups is where links land. */
const SETTINGS_SECTIONS = ["changelog", "ops"] as const;
type AdminSettingsSection = (typeof SETTINGS_SECTIONS)[number];

const adminSearchSchema = z.object({
  // `changelog` was the single tab that deploys and roundups were split out of;
  // links to it are still in Slack threads and Jira tickets.
  tab: z.preprocess(
    (value) => (value === "changelog" ? "deploys" : value),
    z
      .enum([
        "events",
        "deploys",
        "roundups",
        "scheduledJobs",
        "tokenUsage",
        "bigQuery",
        "settings",
      ])
      .catch("events"),
  ),
  /** Which half of the settings tab is open. Absent everywhere else. */
  section: z.enum(SETTINGS_SECTIONS).optional().catch(undefined),
  /**
   * Which group of the operations settings is open. Absent outside that
   * section, and absent there until somebody picks a group — so a link to
   * `section=ops` still lands on the group the panel opens on.
   */
  opsGroup: z.enum(OPS_SETTINGS_GROUPS).optional().catch(undefined),
  /** The same contract as `opsGroup`, for the deploys half of the tab. */
  changelogGroup: z.enum(CHANGELOG_SETTINGS_GROUPS).optional().catch(undefined),
  // Whether a row on the Events tab is one published event or one user action.
  // Actions is the default: "how long did the user wait" is the question the
  // tab exists to answer, and only the grouped view answers it.
  view: z.enum(["events", "actions"]).catch("actions"),
  status: z.enum(["PENDING", "PROCESSING", "COMPLETED", "FAILED"]).optional().catch(undefined),
  eventType: z.string().optional().catch(undefined),
  topic: z.string().optional().catch(undefined),
  actorUserId: z.string().optional().catch(undefined),
  q: z.string().optional().catch(undefined),
  // Absent until the admin touches the toggle: the opening value comes from
  // the ops settings, and pinning it in the URL would override every future
  // change to that default.
  hidePings: z.boolean().optional().catch(undefined),
  // Scopes the table, the KPI window and the chart together, so all three
  // describe the same span of history. Absent = the ops settings default.
  range: z.enum(EVENT_TIME_RANGE_VALUES).optional().catch(undefined),
  // Which product surface the token-usage tab is scoped to. Absent everywhere
  // else, and absent there until the admin picks one.
  feature: z.string().optional().catch(undefined),
  // Which tenant the token-usage tab is scoped to, same contract as `feature`.
  tenantId: z.string().optional().catch(undefined),
  // Which vendor the token-usage tab is scoped to, same contract again.
  provider: z.enum(["GEMINI", "ANTHROPIC"]).optional().catch(undefined),
  // Set by clicking a bucket in the activity chart. In the URL so the slice an
  // admin found is the thing they can paste into a ticket.
  publishedFrom: z.string().optional().catch(undefined),
  publishedTo: z.string().optional().catch(undefined),
  sortBy: z
    .enum(["publishedAt", "processingStartedAt", "processingCompletedAt", "runDurationMs", "eventType", "status", "actorUserId", "topic", "correlationId", "consumerInstanceId"])
    .catch("publishedAt"),
  sortOrder: z.enum(["asc", "desc"]).catch("desc"),
  // The BigQuery tab's narrowings, prefixed so they cannot collide with the
  // token-usage tab's `feature` and `tenantId`. This schema is one flat object
  // shared by every tab, so reusing those names would carry the wrong filter
  // across a tab switch. The window (`range`) is deliberately shared.
  bqFeature: z.string().optional().catch(undefined),
  // The three tenant categories stay distinct: `platform` is work the platform
  // did for itself, `unattributed` is work whose caller was never recorded, and
  // collapsing them would make the unattributed KPI impossible to drill into.
  bqTenantKind: z.enum(BIGQUERY_USAGE_TENANT_KINDS).optional().catch(undefined),
  bqTenantId: z.string().optional().catch(undefined),
  bqFingerprint: z.string().optional().catch(undefined),
  bqCacheHit: z.boolean().optional().catch(undefined),
  // Free text over the statement, job id and correlation id. It is the only
  // narrowing that reaches a row nobody can see yet: the statement breakdown is
  // a top-N list, and an admin arriving with a job id from Google's console or a
  // correlation id from the Events tab has nothing to click.
  bqSearch: z.string().optional().catch(undefined),
  // Set by clicking a chart bucket: an absolute slice that overrides the window.
  bqFrom: z.string().optional().catch(undefined),
  bqTo: z.string().optional().catch(undefined),
  bqSortBy: z.enum(BIGQUERY_USAGE_EXECUTION_SORT_COLUMNS).optional().catch(undefined),
  bqSortOrder: z.enum(["asc", "desc"]).optional().catch(undefined),
  // Which execution's drawer is open, so one query is pasteable into a ticket.
  // The id selects among the rows the API already returned for this admin — it
  // is never fetched by id, because a URL is bookmarkable state, not a grant.
  bqRow: z.string().optional().catch(undefined),
  // Scheduled Jobs narrowings — prefixed so they cannot collide with Events `q`
  // / `eventType` or the token-usage tab. The window (`range`) is shared.
  sjQ: z.string().optional().catch(undefined),
  sjState: z
    .enum(["ENABLED", "PAUSED", "DISABLED", "UPDATE_FAILED", "UNKNOWN"])
    .optional()
    .catch(undefined),
  sjHealth: z
    .enum(["healthy", "failing", "overdue", "untracked"])
    .optional()
    .catch(undefined),
  sjEventType: z.string().optional().catch(undefined),
});

type AdminSearch = z.infer<typeof adminSearchSchema>;

const defaultSearch: AdminSearch = {
  tab: "events",
  view: "actions",
  sortBy: "publishedAt",
  sortOrder: "desc",
};

/**
 * The BigQuery tab's URL position, as the filter object its reads take.
 *
 * Unlike the Events and token-usage tabs, this one has no stored default window
 * in the ops settings, so an absent `range` means the panel-wide default rather
 * than an admin-configured one.
 */
function toBigQueryFilters(search: AdminSearch): BigQueryUsageFilters {
  return {
    windowHours: eventTimeRangeHours(search.range ?? EVENT_TIME_RANGE_DEFAULT),
    ...(search.bqFeature ? { feature: search.bqFeature } : {}),
    ...(search.bqTenantKind ? { tenantKind: search.bqTenantKind } : {}),
    ...(search.bqTenantId ? { tenantId: search.bqTenantId } : {}),
    ...(search.bqFingerprint ? { sqlFingerprint: search.bqFingerprint } : {}),
    ...(search.bqCacheHit === undefined ? {} : { cacheHit: search.bqCacheHit }),
    ...(search.bqSearch ? { search: search.bqSearch } : {}),
    ...(search.bqFrom ? { since: search.bqFrom } : {}),
    ...(search.bqTo ? { until: search.bqTo } : {}),
  };
}

/**
 * The ops settings, or the fallback defaults for a viewer without the grant
 * (or when the read fails — the Events tab should open either way).
 */
async function resolveOpsSettings(queryClient: QueryClient) {
  if (!hasActionPermission("ADMIN_GET_OPS_SETTINGS")) return ADMIN_OPS_FALLBACK;
  try {
    return await queryClient.ensureQueryData(adminOpsSettingsQueryOptions());
  } catch {
    return ADMIN_OPS_FALLBACK;
  }
}

export const Route = createFileRoute("/_authenticated/admin")({
  validateSearch: (search): AdminSearch => {
    const parsed = adminSearchSchema.safeParse(search);
    return parsed.success ? parsed.data : defaultSearch;
  },
  beforeLoad: () => assertPagePermission("admin"),
  // Which tab is open decides what the loader fetches, so it is the only thing
  // the loader depends on. The filters are deliberately excluded: the router
  // shows a full-page pending component at `defaultPendingMs: 0`, so making a
  // loader run on every filter change replaces the whole admin panel with a
  // spinner each time somebody picks a time range. The components below hold
  // their previous data across a filter change and refetch in place instead.
  loaderDeps: ({ search }) => ({ tab: search.tab, section: search.section }),
  // Paired with `loaderDeps`: without it the default zero stale time reloads
  // the match on every navigation, filters included, which is the same problem.
  shouldReload: false,
  loader: async ({ context, location }) => {
    // Block on the access check first — the events endpoints below are
    // super-tenant-only, so prefetching them for a non-admin throws an
    // uncaught 403 that surfaces as the generic crash screen instead of
    // the friendly "no access" page.
    const pageState = await context.queryClient
      .ensureQueryData(pageStateQueryOptions("admin"))
      .catch((err: unknown) => {
        if (err instanceof PageAccessError) return null;
        throw err;
      });

    if (pageState === null) return pageState;

    // Read off the location rather than `deps`, which carries only the tab:
    // these are the filters the tab should open on, and they are read once when
    // it opens rather than on every change to them.
    const parsed = adminSearchSchema.safeParse(location.search);
    const search = parsed.success ? parsed.data : defaultSearch;

    // Each tab pays only for its own data. Both changelog lists are cheap (one
    // DB read); the GitHub round trip happens on the deploys tab's own sync
    // call. The permission check is what keeps a page-level admin without the
    // changelog grant from prefetching a 403 into the crash screen.
    if (search.tab === "deploys") {
      if (hasActionPermission("ADMIN_LIST_CHANGELOG")) {
        await context.queryClient.fetchQuery(changelogQueryOptions());
      }
      return pageState;
    }

    if (search.tab === "settings") {
      if (search.section === "ops") {
        if (hasActionPermission("ADMIN_GET_OPS_SETTINGS")) {
          await context.queryClient.fetchQuery(adminOpsSettingsQueryOptions());
        }
      } else if (hasActionPermission("ADMIN_GET_CHANGELOG_SETTINGS")) {
        await context.queryClient.fetchQuery(changelogSettingsQueryOptions());
      }
      return pageState;
    }

    if (search.tab === "roundups") {
      if (hasActionPermission("ADMIN_LIST_CHANGELOG_DIGESTS")) {
        await context.queryClient.fetchQuery(changelogDigestsQueryOptions());
      }
      return pageState;
    }

    if (search.tab === "scheduledJobs") {
      // Cloud Scheduler is platform infra with no tenant dimension, so
      // `GET /admin/scheduled-jobs` is gated on super-tenant rather than a
      // grantable permission — same rule `hasActionPermission` already
      // applies for the super-tenant bypass.
      const isSuperTenant = useAuth
        .getState()
        .tenants.some((tenant) => tenant.type === "SUPER_TENANT");
      if (isSuperTenant) {
        await context.queryClient.fetchQuery(
          scheduledJobsQueryOptions(
            eventTimeRangeHours(search.range ?? SCHEDULED_JOBS_TIME_RANGE_DEFAULT),
          ),
        );
      }
      return pageState;
    }

    if (search.tab === "tokenUsage") {
      // Cross-tenant spend, so the endpoints are super-tenant-only for the same
      // reason schedules are — prefetching them for anyone else is a 403 into
      // the crash screen.
      const isSuperTenant = useAuth
        .getState()
        .tenants.some((tenant) => tenant.type === "SUPER_TENANT");
      if (isSuperTenant) {
        const ops = await resolveOpsSettings(context.queryClient);
        const windowHours = eventTimeRangeHours(
          search.range ?? eventTimeRangeFromHours(ops.tokenUsageDefaultWindowHours),
        );
        const usageFilters = {
          windowHours,
          ...(search.feature ? { feature: search.feature } : {}),
          ...(search.tenantId ? { tenantId: search.tenantId } : {}),
          ...(search.provider ? { provider: search.provider } : {}),
        };
        // Vertex is deliberately not prefetched: it is a Cloud Monitoring round
        // trip for a panel below the fold, and the tab must open at the same
        // speed whether or not Monitoring is reachable.
        await Promise.all([
          context.queryClient.fetchQuery(llmUsageStatsQueryOptions(usageFilters)),
          context.queryClient.fetchQuery(llmUsageTimeseriesQueryOptions(usageFilters)),
        ]);
      }
      return pageState;
    }

    if (search.tab === "bigQuery") {
      // Cross-tenant warehouse spend, super-tenant-only for the same reason
      // token spend is — prefetching it for anyone else is a 403 into the crash
      // screen.
      const isSuperTenant = useAuth
        .getState()
        .tenants.some((tenant) => tenant.type === "SUPER_TENANT");
      if (isSuperTenant) {
        const filters = toBigQueryFilters(search);
        // Only the two reads the tab leads with. Each of the remaining five
        // expands into several statements against the API's shared connection
        // pool, and the panel already holds them until the first result lands;
        // prefetching them here would undo that bound.
        await Promise.all([
          context.queryClient.fetchQuery(bigQueryUsageStatsQueryOptions(filters)),
          context.queryClient.fetchQuery(bigQueryUsageTimeseriesQueryOptions(filters)),
        ]);
      }
      return pageState;
    }

    // What the tab opens showing — pings hidden or not, and how far back —
    // comes from the ops settings until the URL pins an explicit choice.
    const ops = await resolveOpsSettings(context.queryClient);
    const hidePings = search.hidePings ?? ops.eventsHidePingsDefault;
    const range = search.range ?? eventTimeRangeFromHours(ops.eventsDefaultWindowHours);
    const windowHours = eventTimeRangeHours(range);

    const sharedListFilters = {
      eventTypeIn: search.eventType ? [search.eventType] : undefined,
      topic: search.topic,
      actorUserId: search.actorUserId,
      q: search.q,
      hidePings,
      windowHours,
      publishedFrom: search.publishedFrom,
      publishedTo: search.publishedTo,
      sortOrder: search.sortOrder,
      limit: EVENTS_PAGE_SIZE,
    };

    // Only the view that is about to render is prefetched; the other one's
    // query is disabled until the toggle switches, so fetching it here would
    // buy a round trip nothing reads.
    const fetchList = () =>
      search.view === "actions"
        ? context.queryClient.fetchInfiniteQuery(
            eventActionsInfiniteQueryOptions(sharedListFilters),
          )
        : context.queryClient.fetchInfiniteQuery(
            eventsInfiniteQueryOptions({
              ...sharedListFilters,
              statusIn: search.status ? [search.status] : undefined,
              sortBy: search.sortBy,
            }),
          );

    // Status and the published range are absent by design: the chart plots status
    // as its stacked series and owns the range, so narrowing by either would make
    // it describe its own selection. The window is the exception — it is what the
    // chart is drawn across.
    const activityOptions = eventsTimeseriesQueryOptions({
      eventTypeIn: search.eventType ? [search.eventType] : undefined,
      topic: search.topic,
      actorUserId: search.actorUserId,
      q: search.q,
      hidePings,
      windowHours,
    });

    await context.queryClient.invalidateQueries({ queryKey: eventsKeys.all });
    await Promise.all([
      fetchList(),
      context.queryClient.fetchQuery(eventsUsersQueryOptions()),
      context.queryClient.fetchQuery(eventsStatsQueryOptions(windowHours)),
      context.queryClient.fetchQuery(activityOptions),
    ]);

    return pageState;
  },
  component: AdminRoute,
  pendingComponent: AdminPendingComponent,
  errorComponent: ({ error }) => (
    <PageError is403={error instanceof PageAccessError} />
  ),
});

/**
 * The tab strip stays live while the loader runs.
 *
 * The loader awaits its tab's data and reruns on every tab change, so without
 * this the router's full-page pending loader replaces the whole panel — header
 * included — each time somebody picks a tab, and the tab they just clicked
 * disappears while they wait for it. Rendering the real header over a
 * tab-shaped skeleton keeps the switch itself usable.
 */
function AdminPendingComponent() {
  // The URL is already at the destination once pending renders, so this is the
  // tab being loaded rather than the one being left.
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  return (
    <AdminShell
      activeTab={search.tab}
      onTabChange={(tab) => void navigate({ search: { ...search, tab }, replace: true })}
    >
      <AdminTabSkeleton tab={search.tab} />
    </AdminShell>
  );
}

/** The header and its tab panel, shared by the loaded page and the skeleton. */
function AdminShell({
  activeTab,
  onTabChange,
  children,
}: {
  activeTab: AdminTab;
  onTabChange: (tab: AdminTab) => void;
  children: React.ReactNode;
}) {
  return (
    <div className="flex min-h-0 flex-1 flex-col bg-content-background">
      <AdminTabStrip activeTab={activeTab} onTabChange={onTabChange} />

      <section
        role="tabpanel"
        id={adminTabPanelId(activeTab)}
        aria-labelledby={adminTabId(activeTab)}
        className="flex min-h-0 flex-1 flex-col gap-4 px-6 pb-6 pt-4"
      >
        {children}
      </section>
    </div>
  );
}

function AdminRoute() {
  const { t } = useTranslation();
  useDocumentTitle(t("admin.header.title", "Admin"));
  const { isLoading, error } = usePageState("admin");
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const isSuperTenant = useIsSuperTenant();
  const perms = usePermissions();

  // Loaded once here so the Events tab opens on the stored defaults; a viewer
  // without the grant gets the same defaults a fresh install shows.
  const { data: opsSettings } = useQuery({
    ...adminOpsSettingsQueryOptions(),
    enabled: isSuperTenant || perms.ADMIN_GET_OPS_SETTINGS.isOn,
  });
  const ops = opsSettings ?? ADMIN_OPS_FALLBACK;

  const setTab = (tab: AdminTab) => {
    void navigate({ search: { ...search, tab }, replace: true });
  };

  if (isLoading) {
    return (
      <AdminShell activeTab={search.tab} onTabChange={setTab}>
        <AdminTabSkeleton tab={search.tab} />
      </AdminShell>
    );
  }
  if (error) return <PageError is403={error instanceof PageAccessError} />;

  const settingsSection: AdminSettingsSection = search.section ?? "changelog";
  const hidePings = search.hidePings ?? ops.eventsHidePingsDefault;
  const range = search.range ?? eventTimeRangeFromHours(ops.eventsDefaultWindowHours);
  // Each tab opens on its own stored window, so the shared `range` param falls
  // back differently depending on which one is showing.
  const tokenUsageRange = search.range ?? eventTimeRangeFromHours(ops.tokenUsageDefaultWindowHours);
  // The BigQuery tab has no stored default window of its own, so it falls back
  // to the panel-wide one.
  const bigQueryRange = search.range ?? EVENT_TIME_RANGE_DEFAULT;

  return (
    <AdminShell activeTab={search.tab} onTabChange={setTab}>
      {search.tab === "deploys" ? (
        <ChangelogDeploysPage onRoundupComposed={() => setTab("roundups")} />
      ) : search.tab === "roundups" ? (
        <ChangelogRoundupsPage />
      ) : search.tab === "scheduledJobs" ? (
        <ScheduledJobsPage
          filters={{
            range: search.range ?? SCHEDULED_JOBS_TIME_RANGE_DEFAULT,
            q: search.sjQ,
            state: search.sjState,
            health: search.sjHealth,
            eventType: search.sjEventType,
          }}
          onFiltersChange={(next: ScheduledJobsViewFilters) => {
            void navigate({
              search: {
                tab: search.tab,
                range: next.range,
                sjQ: next.q,
                sjState: next.state,
                sjHealth: next.health,
                sjEventType: next.eventType,
                view: search.view,
                sortBy: search.sortBy,
                sortOrder: search.sortOrder,
              },
              replace: true,
            });
          }}
        />
      ) : search.tab === "tokenUsage" ? (
        // A URL is bookmarkable state, not proof of access. The tab is hidden
        // and unprefetched for everyone else, and a deep link is turned away
        // here rather than left to fail as an uncaught 403 on the crash screen.
        isSuperTenant ? (
          <LlmUsagePage
          filters={{
            range: tokenUsageRange,
            feature: search.feature,
            tenantId: search.tenantId,
            provider: search.provider,
          }}
          onFiltersChange={(next) => {
            void navigate({
              search: {
                tab: search.tab,
                range: next.range,
                feature: next.feature,
                tenantId: next.tenantId,
                provider: next.provider,
                // Sort and row grouping belong to the Events tab, but the
                // schema always carries them; dropping them here would reset
                // that tab's own view.
                view: search.view,
                sortBy: search.sortBy,
                sortOrder: search.sortOrder,
              },
              replace: true,
            });
          }}
        />
        ) : (
          <PageError is403 />
        )
      ) : search.tab === "bigQuery" ? (
        // A URL is bookmarkable state, not proof of access. The tab is hidden
        // and unprefetched for everyone else, and a deep link is turned away
        // here rather than left to fail as an uncaught 403 on the crash screen.
        isSuperTenant ? (
          <BigQueryUsagePage
            filters={{
              range: bigQueryRange,
              feature: search.bqFeature,
              tenantKind: search.bqTenantKind,
              tenantId: search.bqTenantId,
              sqlFingerprint: search.bqFingerprint,
              cacheHit: search.bqCacheHit,
              search: search.bqSearch,
              since: search.bqFrom,
              until: search.bqTo,
              sortBy: search.bqSortBy,
              sortOrder: search.bqSortOrder,
              rowId: search.bqRow,
            }}
            // R24's cutoff, from the settings this route already read for the
            // sibling tabs rather than a second read of its own.
            retentionDays={ops.bigQueryRetentionDays}
            onFiltersChange={(next: BigQueryUsageViewFilters) => {
              void navigate({
                search: {
                  tab: search.tab,
                  range: next.range,
                  bqFeature: next.feature,
                  bqTenantKind: next.tenantKind,
                  bqTenantId: next.tenantId,
                  bqFingerprint: next.sqlFingerprint,
                  bqCacheHit: next.cacheHit,
                  bqSearch: next.search,
                  bqFrom: next.since,
                  bqTo: next.until,
                  bqSortBy: next.sortBy,
                  bqSortOrder: next.sortOrder,
                  bqRow: next.rowId,
                  // Same contract the token-usage tab keeps: the schema always
                  // carries the Events tab's row grouping and sort, and
                  // dropping them here would reset that tab's own view.
                  view: search.view,
                  sortBy: search.sortBy,
                  sortOrder: search.sortOrder,
                },
                replace: true,
              });
            }}
            // The same jump the Events tab makes from an action to its events:
            // the correlation id goes in as the free-text term, on the event
            // view, with pings shown so nothing in the trail is filtered out.
            onOpenCorrelation={(correlationId) => {
              void navigate({
                search: {
                  tab: "events",
                  range: search.range,
                  view: "events",
                  q: correlationId,
                  hidePings: false,
                  sortBy: search.sortBy,
                  sortOrder: search.sortOrder,
                },
              });
            }}
          />
        ) : (
          <PageError is403 />
        )
      ) : search.tab === "settings" ? (
        <>
          <SettingsSectionSwitcher
            active={settingsSection}
            onChange={(section) => {
              void navigate({ search: { ...search, section }, replace: true });
            }}
          />
          {settingsSection === "ops" ? (
            <AdminOpsSettingsPage
              group={search.opsGroup ?? DEFAULT_OPS_SETTINGS_GROUP}
              onGroupChange={(opsGroup) => {
                void navigate({ search: { ...search, opsGroup }, replace: true });
              }}
            />
          ) : (
            <ChangelogSettingsPage
              group={search.changelogGroup ?? DEFAULT_CHANGELOG_SETTINGS_GROUP}
              onGroupChange={(changelogGroup) => {
                void navigate({ search: { ...search, changelogGroup }, replace: true });
              }}
            />
          )}
        </>
      ) : (
        <EventsPage
          stuckAfterMs={ops.eventsStuckAfterMinutes * 60 * 1000}
          slowAfterMs={ops.slowActionSecondsDefault * 1000}
          chartLatencyMetric={ops.eventsChartLatencyMetric}
          showChartBudgetLine={ops.eventsChartShowBudgetLine}
          filters={{
            view: search.view,
            status: search.status as EventJobStatus | undefined,
            eventType: search.eventType,
            topic: search.topic,
            actorUserId: search.actorUserId,
            q: search.q,
            hidePings,
            range,
            publishedFrom: search.publishedFrom,
            publishedTo: search.publishedTo,
            sortBy: search.sortBy as EventJobSortBy,
            sortOrder: search.sortOrder,
          }}
          onFiltersChange={(next) => {
            void navigate({
              search: {
                tab: search.tab,
                view: next.view,
                status: next.status,
                eventType: next.eventType,
                topic: next.topic,
                actorUserId: next.actorUserId,
                q: next.q,
                hidePings: next.hidePings,
                range: next.range,
                publishedFrom: next.publishedFrom,
                publishedTo: next.publishedTo,
                sortBy: next.sortBy,
                sortOrder: next.sortOrder,
              },
              replace: true,
            });
          }}
        />
      )}
    </AdminShell>
  );
}

const SETTINGS_SECTION_LABELS: Record<
  AdminSettingsSection,
  { key: string; fallback: string }
> = {
  changelog: { key: "admin.settingsSections.changelog", fallback: "Releases & roundups" },
  ops: { key: "admin.settingsSections.ops", fallback: "Operations" },
};

/**
 * The settings tab holds two unrelated groups — how deploys reach people, and
 * how the operations tabs behave — so it gets a second, smaller switcher
 * rather than growing the page-level tab strip for what is still one tab.
 *
 * The design-system control rather than a local one: it carries the sliding
 * pill, and its roving focus is a real one. The markup here previously claimed
 * `role="tab"` while offering none of the arrow-key movement that promises,
 * nested inside the page's own tabpanel.
 *
 * `accent="primary"` because this switcher replaces the panel below it. The
 * neutral pill belongs to the range filters that only narrow what is already
 * shown, and reading the same at both jobs would flatten that difference.
 */
function SettingsSectionSwitcher({
  active,
  onChange,
}: {
  active: AdminSettingsSection;
  onChange: (section: AdminSettingsSection) => void;
}) {
  const { t } = useTranslation();

  const options: SegmentedOption[] = SETTINGS_SECTIONS.map((section) => ({
    value: section,
    label: t(SETTINGS_SECTION_LABELS[section].key, SETTINGS_SECTION_LABELS[section].fallback),
  }));

  return (
    <SegmentedControl
      value={active}
      onValueChange={(next) => onChange(next as AdminSettingsSection)}
      options={options}
      accent="primary"
      tone="subtle"
      ariaLabel={t("admin.settingsSections.label", "Settings sections")}
      className="shrink-0"
    />
  );
}
